import { supabase } from '../utils/supabase'

// User Profile Service
export const userService = {
  // Get user profile
  async getProfile(userId) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        *,
        plans (
          id,
          name,
          type,
          price_monthly,
          price_yearly,
          features,
          limits
        )
      `)
      .eq('id', userId)
      .single()

    return { data, error }
  },

  // Create user profile
  async createProfile(userId, profileData) {
    // Get the free plan ID
    const { data: freePlan, error: planError } = await supabase
      .from('plans')
      .select('id')
      .eq('type', 'free')
      .single()

    if (planError) {
      console.warn('⚠️ Could not find free plan, proceeding without plan_id');
    }

    const profile = {
      id: userId,
      email: profileData.email,
      full_name: profileData.full_name || '',
      avatar_url: profileData.avatar_url || '',
      plan_id: freePlan?.id || null,
      ...profileData
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .insert(profile)
      .select()
      .single()

    return { data, error }
  },

  // Update user profile
  async updateProfile(userId, updates) {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    return { data, error }
  },

  // Check search limits
  async checkSearchLimits(userId) {
    const { data, error } = await supabase.rpc('check_search_limits', {
      user_uuid: userId
    })

    return { canSearch: data, error }
  },

  // Get user's search statistics
  async getSearchStats(userId) {
    const today = new Date()
    const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString()
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString()

    const { data: dailyCount, error: dailyError } = await supabase
      .from('market_searches')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', startOfDay)

    const { data: monthlyCount, error: monthlyError } = await supabase
      .from('market_searches')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', startOfMonth)

    const { data: totalCount, error: totalError } = await supabase
      .from('market_searches')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)

    return {
      data: {
        daily: dailyCount || 0,
        monthly: monthlyCount || 0,
        total: totalCount || 0
      },
      error: dailyError || monthlyError || totalError
    }
  }
}

// Plans Service
export const planService = {
  // Get all active plans
  async getPlans() {
    const { data, error } = await supabase
      .from('plans')
      .select('*')
      .eq('is_active', true)
      .order('price_monthly', { ascending: true })

    return { data, error }
  },

  // Get plan by ID
  async getPlan(planId) {
    const { data, error } = await supabase
      .from('plans')
      .select('*')
      .eq('id', planId)
      .single()

    return { data, error }
  }
}

// Market Searches Service
export const searchService = {
  // Get all searches for a user
  async getUserSearches(userId, options = {}) {
    let query = supabase
      .from('market_searches')
      .select('*')
      .eq('user_id', userId)

    if (options.status) {
      query = query.eq('status', options.status)
    }

    if (options.limit) {
      query = query.limit(options.limit)
    }

    query = query.order('created_at', { ascending: false })

    const { data, error } = await query
    return { data, error }
  },

  // Get a specific search
  async getSearch(searchId, userId) {
    const { data, error } = await supabase
      .from('market_searches')
      .select('*')
      .eq('id', searchId)
      .eq('user_id', userId)
      .single()

    return { data, error }
  },

  // Create a new search
  async createSearch(userId, searchData) {
    const search = {
      user_id: userId,
      title: searchData.title || `Search ${new Date().toLocaleDateString()}`,
      original_idea: searchData.original_idea,
      status: 'draft',
      ...searchData
    }

    const { data, error } = await supabase
      .from('market_searches')
      .insert(search)
      .select()
      .single()

    return { data, error }
  },

  // Update a search
  async updateSearch(searchId, userId, updates) {
    const { data, error } = await supabase
      .from('market_searches')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', searchId)
      .eq('user_id', userId)
      .select()
      .single()

    return { data, error }
  },

  // Delete a search
  async deleteSearch(searchId, userId) {
    const { data, error } = await supabase
      .from('market_searches')
      .delete()
      .eq('id', searchId)
      .eq('user_id', userId)

    return { data, error }
  },

  // Mark search as completed
  async completeSearch(searchId, userId, finalData = {}) {
    const { data, error } = await supabase
      .from('market_searches')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        ...finalData
      })
      .eq('id', searchId)
      .eq('user_id', userId)
      .select()
      .single()

    return { data, error }
  },

  // Toggle favorite status
  async toggleFavorite(searchId, userId) {
    // First get current status
    const { data: current } = await supabase
      .from('market_searches')
      .select('is_favorite')
      .eq('id', searchId)
      .eq('user_id', userId)
      .single()

    if (!current) return { data: null, error: { message: 'Search not found' } }

    const { data, error } = await supabase
      .from('market_searches')
      .update({ is_favorite: !current.is_favorite })
      .eq('id', searchId)
      .eq('user_id', userId)
      .select()
      .single()

    return { data, error }
  }
}

// Search Sessions Service
export const sessionService = {
  // Create a new session
  async createSession(searchId, userId, sessionData = {}) {
    const session = {
      search_id: searchId,
      user_id: userId,
      session_data: sessionData,
      step_completed: 0,
      time_spent_seconds: 0
    }

    const { data, error } = await supabase
      .from('search_sessions')
      .insert(session)
      .select()
      .single()

    return { data, error }
  },

  // Update session
  async updateSession(sessionId, updates) {
    const { data, error } = await supabase
      .from('search_sessions')
      .update(updates)
      .eq('id', sessionId)
      .select()
      .single()

    return { data, error }
  },

  // Get sessions for a search
  async getSearchSessions(searchId) {
    const { data, error } = await supabase
      .from('search_sessions')
      .select('*')
      .eq('search_id', searchId)
      .order('created_at', { ascending: false })

    return { data, error }
  }
}

// Utility functions
export const dbUtils = {
  // Test database connection
  async testConnection() {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('count')
        .limit(1)

      return { connected: !error, error }
    } catch (error) {
      return { connected: false, error }
    }
  },

  // Get database status
  async getStatus() {
    const { data: { session } } = await supabase.auth.getSession()
    const { connected } = await this.testConnection()

    return {
      connected,
      authenticated: !!session,
      user: session?.user || null
    }
  }
}